import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import { GraduationCap, User, ArrowRight, Ticket, Gift } from "lucide-react";
import AuthDemoStrip, { ScrollHint } from "../components/AuthDemoStrip";
import { apiClient } from "../lib/api";

/** Reusable badge shown on the register pages when a ?ref=CODE query-string is present.
 *  Tells the new user they were invited and the referral will be applied. */
function ReferralBadge({ code }) {
  const t = useT();
  if (!code) return null;
  return (
    <div
      className="mb-5 rounded-2xl border-2 px-4 py-3 flex items-start gap-3"
      data-testid="referral-applied-badge"
      style={{ borderColor: "var(--app-accent)", background: "rgba(255,183,3,0.10)" }}
    >
      <Gift className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--app-accent)" }} />
      <div className="text-sm leading-tight">
        <div className="font-bold">{t("You were invited!")}</div>
        <div style={{ color: "var(--app-text-muted)" }}>
          {t("Referral code")} <b style={{ color: "var(--app-text)" }}>{code}</b> {t("will be applied. When you subscribe, your friend gets a free month too.")}
        </div>
      </div>
    </div>
  );
}

/** Coming-soon iOS + Android pill badges. Shown on Login + Register-choose pages. */
function ComingSoonBadges() {
  const t = useT();
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="coming-soon-badges">
      <span
        data-testid="coming-soon-apple"
        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border"
        style={{
          borderColor: "var(--app-border)",
          background: "var(--app-surface-2)",
          color: "var(--app-text)",
          opacity: 0.85,
        }}
        title={t("Coming soon to iOS")}
      >
        {/* Apple logo — bitten-apple silhouette */}
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.47-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
        <span>iOS</span>
        <span
          className="ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
          style={{ background: "var(--app-accent)", color: "#0a0a0a" }}
        >
          {t("Coming soon")}
        </span>
      </span>
      <span
        data-testid="coming-soon-android"
        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border"
        style={{
          borderColor: "var(--app-border)",
          background: "var(--app-surface-2)",
          color: "var(--app-text)",
          opacity: 0.85,
        }}
        title={t("Coming soon to Android")}
      >
        {/* Android logo — Bugdroid mascot (CC-BY 3.0, Google) */}
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="#3DDC84" aria-hidden="true">
          <path d="M17.523 15.341a1.013 1.013 0 1 1 0-2.026 1.013 1.013 0 0 1 0 2.026m-11.046 0a1.013 1.013 0 1 1 0-2.026 1.013 1.013 0 0 1 0 2.026m11.405-6.072 2.018-3.494a.42.42 0 0 0-.154-.574.42.42 0 0 0-.573.154L16.13 8.875c-1.5-.685-3.205-1.07-5.13-1.07s-3.63.385-5.13 1.07L3.827 5.355a.421.421 0 0 0-.574-.154.421.421 0 0 0-.154.574l2.018 3.494C1.642 11.107.288 14.434 0 18.001h24c-.288-3.567-1.642-6.894-5.117-8.732" />
        </svg>
        <span>Android</span>
        <span
          className="ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
          style={{ background: "var(--app-accent)", color: "#0a0a0a" }}
        >
          {t("Coming soon")}
        </span>
      </span>
    </div>
  );
}

function Panel({ children }) {
  const t = useT();
  return (
    <div className="min-h-screen w-full flex">
      <div className="hidden lg:flex w-[55%] relative overflow-hidden" style={{ background: "linear-gradient(160deg, var(--app-primary), var(--app-secondary))" }}>
        <div className="absolute inset-0 staff-lines opacity-40" />
        <div className="relative z-10 p-16 flex flex-col justify-between text-white w-full">
          <div>
            <div className="flex items-center gap-4">
              <img src="/octave-icon.png" alt="" aria-hidden className="w-14 h-14 object-contain" />
              <div className="leading-none">
                <div className="font-display font-bold text-3xl tracking-tight">Octave</div>
                <div className="text-xs font-normal opacity-80 mt-1 tracking-wide">{t("Stretch your music muscles")}</div>
              </div>
            </div>
          </div>
          <div>
            <div className="font-play text-5xl leading-tight mb-4 max-w-lg">{t("Track every scale, song, streak and trophy — on every instrument you play.")}</div>
            <p className="opacity-90 max-w-md">{t("Every instrument in the world — piano, guitar, violin, drums, voice, horns and more. One app, all your progress in one place.")}</p>
            <div className="mt-6 text-sm opacity-80">{t("30-day free trial · Cancel anytime")}</div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16" style={{ background: "var(--app-bg)" }}>
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await login(email, password);
      toast.success(`${t("Welcome back,")} ${r.user.name.split(" ")[0]}!`);
      if (r.user.role === "student") nav("/student");
      else if (r.user.role === "developer") nav("/developer");
      else nav("/teacher");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Map common backend strings to translated strings; fall back to detail or generic.
      const localized = detail === "Invalid email or password" ? t("Invalid email or password") : (detail || t("Login failed"));
      toast.error(localized);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Panel>
        <div className="flex flex-col items-center text-center mb-8">
          <img src="/octave-icon.png" alt="" aria-hidden className="w-24 h-24 object-contain" />
          <div className="font-display font-bold text-4xl mt-2 tracking-tight">Octave</div>
          <div className="text-xs mt-1 tracking-wide" style={{ color: "var(--app-text-muted)" }}>{t("Stretch your music muscles")}</div>
        </div>
        <h1 className="font-display text-3xl font-bold mb-2">{t("Welcome back")}</h1>
        <p className="mb-8" style={{ color: "var(--app-text-muted)" }}>{t("Sign in to your studio.")}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label>{t("Email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div>
            <Label>{t("Password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full btn-pill btn-primary h-11 justify-center" data-testid="login-submit">
            {busy ? t("Signing in...") : t("Sign in")}
          </Button>
        </form>
        <div className="mt-3 text-sm text-center">
          <Link
            to="/forgot-password"
            data-testid="link-forgot-password"
            className="font-semibold hover:underline"
            style={{ color: "var(--app-primary)" }}
          >
            {t("Forgot your password?")}
          </Link>
        </div>
        <div className="mt-6 text-sm text-center" style={{ color: "var(--app-text-muted)" }}>
          {t("New here?")}
        </div>
        <Link
          to="/register"
          data-testid="link-register"
          className="mt-2 w-full btn-pill btn-outline h-11 inline-flex items-center justify-center font-bold"
        >
          {t("Create an account →")}
        </Link>
        <ComingSoonBadges />
        <div className="mt-4">
          <ScrollHint />
        </div>
      </Panel>
      <section className="px-6 py-12 lg:py-16" style={{ background: "var(--app-bg)" }}>
        <AuthDemoStrip />
      </section>
    </>
  );
}

export function RegisterChoosePage() {
  const t = useT();
  return (
    <>
      <Panel>
      <h1 className="font-display text-4xl font-bold mb-2">{t("Let's get you set up")}</h1>
      <p className="mb-8" style={{ color: "var(--app-text-muted)" }}>
        {t("First question — which best describes you?")}
      </p>

      <div className="space-y-4" data-testid="register-choose">
        <Link
          to="/register/teacher"
          data-testid="choose-teacher"
          className="block rounded-2xl p-5 border-2 transition-all hover:-translate-y-0.5 hover:shadow-lg"
          style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}
        >
          <div className="flex items-start gap-4">
            <span className="flex items-center justify-center rounded-xl w-12 h-12 shrink-0"
                  style={{ background: "var(--app-primary)", color: "#fff" }}>
              <GraduationCap className="w-6 h-6" />
            </span>
            <div className="flex-1">
              <div className="font-display text-xl font-bold">{t("I'm a teacher")}</div>
              <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
                {t("Run a studio, add students, invoice, plan recitals. 30-day free trial, then $9.99/mo for up to 75 students with unlimited instruments — and your students use the app free.")}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 mt-2 opacity-60" />
          </div>
        </Link>

        <Link
          to="/register/solo"
          data-testid="choose-solo"
          className="block rounded-2xl p-5 border-2 transition-all hover:-translate-y-0.5 hover:shadow-lg"
          style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}
        >
          <div className="flex items-start gap-4">
            <span className="flex items-center justify-center rounded-xl w-12 h-12 shrink-0"
                  style={{ background: "#FFB703", color: "#0a0a0a" }}>
              <User className="w-6 h-6" />
            </span>
            <div className="flex-1">
              <div className="font-display text-xl font-bold">{t("I'm a musician")}</div>
              <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
                {t("Track your own practice, songs, scales, streaks and trophies across every instrument you play — piano, guitar, violin, drums and more. 30-day free trial, then $2.99/mo.")}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 mt-2 opacity-60" />
          </div>
        </Link>

        <Link
          to="/join"
          data-testid="choose-has-code"
          className="block rounded-2xl p-4 border-2 transition-all hover:-translate-y-0.5"
          style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}
        >
          <div className="flex items-center gap-3">
            <Ticket className="w-5 h-5" />
            <div className="flex-1">
              <div className="font-bold">{t("I have an invite code from my teacher")}</div>
              <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>{t("Join your teacher's studio.")}</p>
            </div>
            <ArrowRight className="w-4 h-4 opacity-60" />
          </div>
        </Link>
      </div>

      <div className="mt-8 text-sm">
        {t("Already have an account?")} <Link className="font-semibold" style={{ color: "var(--app-primary)" }} to="/login" data-testid="link-login">{t("Sign in")}</Link>
      </div>

      <ComingSoonBadges />
      <div className="mt-4">
        <ScrollHint />
      </div>
    </Panel>
    <section className="px-6 py-12 lg:py-16" style={{ background: "var(--app-bg)" }}>
      <AuthDemoStrip />
    </section>
    </>
  );
}

export function RegisterSoloStudentPage() {
  const { registerSoloStudent } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error(t("Passwords don't match.")); return; }
    setBusy(true);
    try {
      await registerSoloStudent({ name, email, password, instruments: [], referral_code: refCode || undefined });
      toast.success(t("You're in! Let's make some music. 🎶"));
      nav("/student");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Registration failed"));
    } finally { setBusy(false); }
  };

  return (
    <Panel>
      <Link to="/register" className="text-sm font-semibold mb-4 inline-block" style={{ color: "var(--app-primary)" }}>{t("← Back")}</Link>
      <h1 className="font-display text-4xl font-bold mb-2">{t("Start practicing")}</h1>
      <p className="mb-6" style={{ color: "var(--app-text-muted)" }}>
        {t("Track songs, scales, streaks & trophies — on every instrument you play. 30-day free trial, then $2.99/mo.")}
      </p>
      <ReferralBadge code={refCode} />
      <form onSubmit={submit} className="space-y-4" data-testid="solo-register-form">
        <div><Label>{t("Your name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="solo-name" /></div>
        <div><Label>{t("Email")}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="solo-email" /></div>
        <div><Label>{t("Password")}</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="solo-password" /></div>
        <div>
          <Label>{t("Confirm password")}</Label>
          <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="solo-confirm-password" />
          {confirmPassword && confirmPassword !== password && (
            <p className="text-xs mt-1" style={{ color: "#dc2626" }} data-testid="solo-password-mismatch">{t("Passwords don't match.")}</p>
          )}
        </div>
        <Button type="submit" disabled={busy || (confirmPassword !== "" && confirmPassword !== password)} className="w-full btn-pill btn-primary h-11 justify-center" data-testid="solo-submit">
          {busy ? t("Creating...") : t("Start free 30-day trial")}
        </Button>
      </form>
      <div className="mt-6 text-sm">
        {t("Have a teacher's invite code?")} <Link className="font-semibold" style={{ color: "var(--app-primary)" }} to="/join" data-testid="link-join">{t("Join their studio")}</Link>
      </div>
    </Panel>
  );
}

export function RegisterTeacherPage() {
  const { registerTeacher } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error(t("Passwords don't match.")); return; }
    setBusy(true);
    try {
      const r = await registerTeacher({ name, email, password, instruments: [], referral_code: refCode || undefined });
      toast.success(t("Studio created! 30-day free trial active."));
      nav(r.user.role === "developer" ? "/developer" : "/teacher");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Registration failed"));
    } finally { setBusy(false); }
  };

  return (
    <Panel>
      <Link to="/register" className="text-sm font-semibold mb-4 inline-block" style={{ color: "var(--app-primary)" }}>{t("← Back")}</Link>
      <h1 className="font-display text-4xl font-bold mb-2">{t("Start your studio")}</h1>
      <p className="mb-8" style={{ color: "var(--app-text-muted)" }}>{t("Free 30-day trial. No credit card required.")}</p>
      <ReferralBadge code={refCode} />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label>{t("Your name")}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" />
        </div>
        <div>
          <Label>{t("Email")}</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" />
        </div>
        <div>
          <Label>{t("Password")}</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" />
        </div>
        <div>
          <Label>{t("Confirm password")}</Label>
          <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="register-confirm-password" />
          {confirmPassword && confirmPassword !== password && (
            <p className="text-xs mt-1" style={{ color: "#dc2626" }} data-testid="register-password-mismatch">{t("Passwords don't match.")}</p>
          )}
        </div>
        <Button type="submit" disabled={busy || (confirmPassword !== "" && confirmPassword !== password)} className="w-full btn-pill btn-primary h-11 justify-center" data-testid="register-submit">
          {busy ? t("Creating...") : t("Create studio")}
        </Button>
      </form>
      <div className="mt-6 text-sm">
        {t("Already have an account?")} <Link className="font-semibold" style={{ color: "var(--app-primary)" }} to="/login" data-testid="link-login">{t("Sign in")}</Link>
      </div>
    </Panel>
  );
}

export function StudentJoinPage() {
  const { registerStudent } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlCode = window.location.pathname.split("/").pop();
  const [inviteCode, setInviteCode] = useState(urlCode && urlCode !== "join" ? urlCode : (params.get("code") || ""));
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error(t("Passwords don't match.")); return; }
    setBusy(true);
    try {
      await registerStudent({ invite_code: inviteCode, name, email, password, instruments: [] });
      toast.success(t("Welcome to your music journey! 🎶"));
      nav("/student");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Join failed"));
    } finally { setBusy(false); }
  };

  return (
    <Panel>
      <h1 className="font-display text-4xl font-bold mb-2">{t("Join your teacher's studio")}</h1>
      <p className="mb-6" style={{ color: "var(--app-text-muted)" }}>{t("Enter the invite code your teacher gave you.")}</p>
      <form onSubmit={submit} className="space-y-4" data-testid="student-join-form">
        <div>
          <Label>{t("Invite code")}</Label>
          <Input value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} required data-testid="join-code" />
        </div>
        <div>
          <Label>{t("Your name")}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="join-name" />
        </div>
        <div>
          <Label>{t("Email")}</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="join-email" />
        </div>
        <div>
          <Label>{t("Password")}</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="join-password" />
        </div>
        <div>
          <Label>{t("Confirm password")}</Label>
          <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="join-confirm-password" />
          {confirmPassword && confirmPassword !== password && (
            <p className="text-xs mt-1" style={{ color: "#dc2626" }} data-testid="join-password-mismatch">{t("Passwords don't match.")}</p>
          )}
        </div>
        <Button type="submit" disabled={busy || (confirmPassword !== "" && confirmPassword !== password)} className="w-full btn-pill btn-primary h-11 justify-center" data-testid="join-submit">
          {busy ? t("Joining...") : t("Start practicing")}
        </Button>
      </form>
    </Panel>
  );
}


export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Endpoint is designed to never reveal — but if the network fails, show a generic toast.
      toast.error(err?.response?.data?.detail || t("Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <Link to="/login" className="text-sm font-semibold mb-4 inline-block" style={{ color: "var(--app-primary)" }}>{t("← Back to sign in")}</Link>
      <h1 className="font-display text-4xl font-bold mb-2">{t("Forgot password?")}</h1>
      <p className="mb-8" style={{ color: "var(--app-text-muted)" }}>
        {t("Enter the email you signed up with and we'll send you a link to reset your password.")}
      </p>
      {sent ? (
        <div
          data-testid="forgot-password-sent"
          className="rounded-2xl border-2 p-5"
          style={{ borderColor: "var(--app-accent)", background: "rgba(255,183,3,0.10)" }}
        >
          <div className="font-bold mb-1">{t("Check your inbox")}</div>
          <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>
            {t("If an account exists for that email, a reset link is on its way. The link is good for 30 minutes.")}
          </p>
          <div className="mt-4 text-sm">
            <Link to="/login" className="font-semibold" style={{ color: "var(--app-primary)" }}>{t("Back to sign in")}</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
          <div>
            <Label>{t("Email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              data-testid="forgot-password-email"
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full btn-pill btn-primary h-11 justify-center" data-testid="forgot-password-submit">
            {busy ? t("Sending...") : t("Send reset link")}
          </Button>
        </form>
      )}
    </Panel>
  );
}

export function ResetPasswordPage() {
  const t = useT();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error(t("Passwords don't match.")); return; }
    if (!token) { toast.error(t("This reset link is invalid.")); return; }
    setBusy(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success(t("Password updated. You can sign in now."));
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("Couldn't reset your password. The link may have expired."));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Panel>
        <h1 className="font-display text-4xl font-bold mb-2">{t("Reset password")}</h1>
        <div className="mt-4 rounded-2xl border-2 p-5" style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}>
          <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>
            {t("This reset link is invalid or has expired.")}
          </p>
          <div className="mt-4 text-sm">
            <Link to="/forgot-password" className="font-semibold" style={{ color: "var(--app-primary)" }}>{t("Request a new link")}</Link>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel>
      <h1 className="font-display text-4xl font-bold mb-2">{t("Reset password")}</h1>
      <p className="mb-8" style={{ color: "var(--app-text-muted)" }}>{t("Pick a new password — at least 6 characters.")}</p>
      {done ? (
        <div className="rounded-2xl border-2 p-5" style={{ borderColor: "var(--app-accent)", background: "rgba(255,183,3,0.10)" }} data-testid="reset-password-done">
          <div className="font-bold">{t("All set!")}</div>
          <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>{t("Redirecting you to sign in...")}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <div>
            <Label>{t("New password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoFocus data-testid="reset-password-new" />
          </div>
          <div>
            <Label>{t("Confirm new password")}</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="reset-password-confirm" />
            {confirmPassword && confirmPassword !== password && (
              <p className="text-xs mt-1" style={{ color: "#dc2626" }} data-testid="reset-password-mismatch">{t("Passwords don't match.")}</p>
            )}
          </div>
          <Button type="submit" disabled={busy || (confirmPassword !== "" && confirmPassword !== password)} className="w-full btn-pill btn-primary h-11 justify-center" data-testid="reset-password-submit">
            {busy ? t("Updating...") : t("Update password")}
          </Button>
        </form>
      )}
    </Panel>
  );
}
